// features/projects/templates/continuum/species-dashboard.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM SPECIES DASHBOARD — domain-specific lens
//
// Originally the Continuum's main Dashboard tab (the synthesis
// card grid replaced it 2026-05-03); kept and renamed because
// the species/ecosystem view is a separate analytical surface
// worth preserving on its own tab.
//
// Port of signal_dashboard.html. The Signal palette (green-tinted)
// is intentionally different from the Continuum cool palette used
// by the rest of the template — see tokens.js for context.
//
// Layout:
//   ┌────────┬───────────────────────────────────────────┐
//   │        │  Eco header (title + 5 metric cards)      │
//   │ Sidebar├──────────────────────────┬────────────────┤
//   │  · Eco │   Species cards grid     │  Right panel   │
//   │  · Int │   (10 indicator species  │  (Cascade /    │
//   │  · Src │    with sparklines +     │   Story /      │
//   │  · Bld │    status badges)        │   Intervention)│
//   │        ├──────────────────────────┴────────────────┤
//   │        │  Bottom strip — data source statuses      │
//   └────────┴───────────────────────────────────────────┘
//
// All data here is MOCKED 1:1 from the source HTML so the visuals
// are pixel-faithful. Live data wiring lands incrementally as the
// 6-month research build progresses.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { S, SIGNAL_FONT as F } from './tokens';

// ─── Static navigation (sub-views, not data) ──────────────────
const ECOSYSTEMS = [
  { id: 'everglades', label: 'Florida Everglades', dot: 'amber', active: true },
  { id: 'atlantic',   label: 'Atlantic Forest',    dot: 'dim',   planned: true },
  { id: 'cerrado',    label: 'Cerrado',            dot: 'dim',   planned: true },
];

const INTELLIGENCE = [
  { id: 'species',       label: '◈ Species Dashboard' },
  { id: 'cascades',      label: '◇ Cascade Models' },
  { id: 'stories',       label: '◎ Story Engine' },
  { id: 'interventions', label: '▦ Interventions' },
];

// ─── SPECIES_DETAIL — authored prose for the modal (Wave 2 will
// replace with Shift-generated descriptions sourced from real data).
// Keyed by binomial so the live data join matches.
const SPECIES_DETAIL_BY_BINOMIAL = {
  'Puma concolor coryi':         { what: '', why: '', sources: '' },
  'Rostrhamus sociabilis':       { what: '', why: '', sources: '' },
  'Pristis pectinata':           { what: '', why: '', sources: '' },
  'Ammodramus mirabilis':        { what: '', why: '', sources: '' },
  'Trichechus manatus':          { what: '', why: '', sources: '' },
  'Mycteria americana':          { what: '', why: '', sources: '' },
  'Platalea ajaja':              { what: '', why: '', sources: '' },
  'Eumops floridanus':           { what: '', why: '', sources: '' },
  'Alligator mississippiensis':  { what: '', why: '', sources: '' },
  'Crocodylus acutus':           { what: '', why: '', sources: '' },
};
// Fill SPECIES_DETAIL_BY_BINOMIAL from the legacy keyed-by-id mocks below.
// TODO Wave 2: replace this block with Shift-generated text from a
// continuum_species_descriptions table populated by the Cascade /
// Story / Intervention generators.
const _LEGACY_SPECIES_DETAIL = {
  panther:  { trend:[100,96,91,88,84,79,74,69,65,59], color:S.terra, statusFull:'Critically Endangered',
              what:'The Florida Panther is the apex predator of the Everglades. It controls deer and wild hog populations, preventing overgrazing that destroys marsh vegetation. As the top of the food web, its health reflects the health of every layer beneath it.',
              why:'Apex predator loss triggers trophic cascade. Without Panther predation pressure, deer and hog populations explode, destroying the marsh grass and tree islands that 90+ bird species depend on for nesting. The Panther is the system\'s top regulator.',
              sources:'GBIF occurrence data 2014-2024 · IUCN Red List 2022 assessment · Florida Fish and Wildlife Conservation Commission telemetry data · USFWS Panther Recovery Plan 2023' },
  kite:     { trend:[100,92,84,76,66,54,44,36,30,32], color:S.terra, statusFull:'Endangered',
              what:'The Snail Kite is one of the most specialized birds in North America — it eats only apple snails. This makes it an extraordinarily precise indicator of water quality and aquatic vegetation health. When water levels are right and plants are healthy, apple snails thrive and Kites flourish.',
              why:'The Kite is the trigger species for this cascade model. Its decline signals apple snail population instability — which begins the chain from aquatic vegetation collapse through fish decline to $1.2B in downstream economic impact. The Kite is a living water quality sensor.',
              sources:'GBIF occurrence data 2010-2024 · iNaturalist research-grade observations · IUCN Red List 2022 · USFWS Snail Kite Survey annual reports · Bennetts et al. 2006 population modeling' },
  sawfish:  { trend:[100,88,74,58,42,30,20,12,8,5], color:S.terra, statusFull:'Critically Endangered',
              what:'The Smalltooth Sawfish is a marine fish that uses its long toothed rostrum to stun fish and invertebrates. It is a keystone species in coastal and estuarine ecosystems, controlling forage fish populations and cycling nutrients between marine and freshwater zones.',
              why:'The last viable US population is in Southwest Florida. Its continued presence indicates healthy estuarine connectivity between the Everglades and the Gulf. Loss would signal complete estuarine function collapse — affecting commercial fisheries worth $400M annually to Florida.',
              sources:'GBIF occurrence records · IUCN Red List 2019 · NOAA Fisheries recovery plan · Carlson et al. 2013 historical distribution analysis · Florida Museum of Natural History sawfish database' },
  sparrow:  { trend:[100,89,78,64,48,34,24,18,14,17], color:S.terra, statusFull:'Critically Endangered',
              what:'Found nowhere else on Earth. The Cape Sable Seaside Sparrow nests only in specific marl prairie habitats of the Everglades — a rare, low-elevation grassland maintained by precise water level fluctuations. It is the most range-restricted bird in the continental US.',
              why:'An endemism signal — if it disappears here, it disappears from Earth. Its nesting habitat requirements are so specific that its population trend is a precise measure of whether Everglades water management is working at the most granular level.',
              sources:'USFWS annual survey 1992-2024 · GBIF observations · Pimm et al. 2002 · South Florida Ecosystem Restoration Task Force annual reports · Nott et al. 1998 habitat analysis' },
  manatee:  { trend:[80,88,94,100,97,93,88,82,77,78], color:S.amber, statusFull:'Vulnerable',
              what:'The Florida Manatee is a large herbivore that feeds on seagrass beds. It controls seagrass growth and prevents overgrowth that can crowd out diversity. Manatees are "sentinel species" — their health directly reflects water temperature, water quality, and seagrass availability.',
              why:'Manatee mass mortality events in 2021-2022 (over 1,000 deaths) were caused by seagrass collapse in Indian River Lagoon from nutrient pollution. This is the human-scale story — nutrient runoff from agriculture killed the seagrass, the manatees starved, and 8M Florida residents saw it happen in their backyard.',
              sources:'USFWS manatee survey 2000-2024 · Florida Fish and Wildlife aerial survey data · IUCN Red List 2020 · Reid et al. 2003 · NOAA Fisheries status review 2022' },
  stork:    { trend:[100,96,92,90,88,85,83,81,80,82], color:S.amber, statusFull:'Near Threatened',
              what:'The Wood Stork is a tactile feeder — it finds fish by touch, not sight. This means it requires high fish density in concentrated areas to feed successfully. It can only breed successfully when water levels recede precisely in spring, concentrating fish in shrinking pools. It is the Everglades\' hydrology indicator.',
              why:'Wood Stork nesting success is the single best annual indicator of whether the Everglades water management system is functioning correctly. A bad nesting year (low chick production) means water receded too fast, too slow, or at the wrong time. It is a living report card for the entire restoration effort.',
              sources:'Audubon Christmas Bird Count 1980-2024 · eBird species data · USFWS Wood Stork recovery plan · Bildstein et al. 1990 · Florida Audubon Society colonial waterbird monitoring' },
  spoonbill:{ trend:[100,98,95,92,90,88,86,85,84,85], color:S.amber, statusFull:'Least Concern',
              what:'The Roseate Spoonbill feeds by sweeping its spoon-shaped bill through shallow water, catching small fish and crustaceans. Its vivid pink coloration makes it the most visually striking bird in Florida. Its presence or absence is immediately visible to the public — making it the ecosystem\'s most effective ambassador.',
              why:'The Spoonbill is the story species — the one the public cares about. Its decline is visible and emotionally impactful in a way that a 34% decline in macroinvertebrate density is not. When the Spoonbill population falls, the story is already written.',
              sources:'Florida Audubon monitoring 1940-2024 · GBIF occurrence data · iNaturalist observations · Dumas 2000 · Lorenz et al. 2002 Florida Bay ecosystem study' },
  bat:      { trend:[100,93,87,80,73,66,60,54,50,71], color:S.amber, statusFull:'Endangered',
              what:'The largest bat in Florida. A nighttime insect predator that can consume thousands of insects per night including agricultural pests. Found only in South Florida. Roosts in tree cavities of old-growth cypress and mangrove. Requires large, contiguous habitat for foraging routes.',
              why:'An indicator of habitat fragmentation. As the Everglades fragments into isolated patches separated by developed land, the Florida Bonneted Bat — which needs large connected territories — is one of the first mammals to show population pressure. Its decline signals the fragmentation threshold approaching.',
              sources:'IUCN Red List 2016 · Florida Bat Conservancy acoustic monitoring 2019-2024 · Timm and Genoways 2004 · USFWS candidate species review · University of Florida bat monitoring program' },
  alligator:{ trend:[70,76,82,88,92,95,97,99,100,100], color:S.forest, statusFull:'Least Concern',
              what:'The alligator is the Everglades\' ecosystem engineer. It creates "alligator holes" — depressions dug in the marsh bottom that hold water year-round. These holes become refugia for fish, turtles, birds, and invertebrates during the dry season. Without alligator holes, dry season mortality would collapse aquatic life.',
              why:'Stable population is good news and important context. The alligator recovery from near-extinction in the 1960s shows that targeted conservation intervention works. It is a success story to anchor the narrative — the system can recover when given the right conditions.',
              sources:'Florida Fish and Wildlife survey 1977-2024 · IUCN Red List 2019 · Mazzotti and Brandt 1994 · USGS Everglades National Park monitoring · GBIF occurrence data' },
  croc:     { trend:[30,36,44,52,60,68,75,82,88,94], color:S.water, statusFull:'Vulnerable',
              what:'The American Crocodile is the only crocodilian in the US and shares the Everglades with the alligator. It occupies coastal and estuarine zones — the transition zone between freshwater marsh and marine environment. Its recovery indicates improving water quality at the coast.',
              why:'A recovery success story that validates the cascade model in reverse. As water management has improved in some coastal zones, crocodile populations have responded. This is the evidence that intervention works — showing funders and partners that the system responds to management.',
              sources:'IUCN Red List 2012 · Mazzotti et al. 2007 recovery assessment · USFWS five-year review 2023 · Florida Fish and Wildlife crocodile monitoring program 1975-2024' },
};

// Map legacy id-keyed mocks → binomial-keyed lookup so the live data
// join finds them. Adapter to be deleted in Wave 2.
const _LEGACY_ID_TO_BINOMIAL = {
  panther:   'Puma concolor coryi',
  kite:      'Rostrhamus sociabilis',
  sawfish:   'Pristis pectinata',
  sparrow:   'Ammodramus mirabilis',
  manatee:   'Trichechus manatus',
  stork:     'Mycteria americana',
  spoonbill: 'Platalea ajaja',
  bat:       'Eumops floridanus',
  alligator: 'Alligator mississippiensis',
  croc:      'Crocodylus acutus',
};
for (const [id, bin] of Object.entries(_LEGACY_ID_TO_BINOMIAL)) {
  if (_LEGACY_SPECIES_DETAIL[id]) SPECIES_DETAIL_BY_BINOMIAL[bin] = _LEGACY_SPECIES_DETAIL[id];
}

// Wave 2 fully shipped — Cascade, Story, Intervention all live
// (continuum_cascades / continuum_stories / continuum_interventions
// tables with Shift-generated drafts via Sonnet 4.6).

// ─── Component ───────────────────────────────────────────
export default function ContinuumSpeciesDashboard({ workspaceId, onNavigate }) {
  const [selectedBinomial, setSelectedBinomial] = useState(null);
  const [activeRpTab, setActiveRpTab] = useState('cascade');
  const [detailBinomial, setDetailBinomial] = useState(null);
  // Intelligence sub-view: which sidebar item is active.
  // 'species' = the original 3-column dashboard (default).
  // 'cascades' / 'stories' / 'interventions' = full-width libraries.
  const [intelligenceView, setIntelligenceView] = useState('species');

  // ── Live data from /api/continuum/dashboard ──
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/continuum/dashboard?workspace_id=${workspaceId}`);
        if (!r.ok) throw new Error(`dashboard ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (err) {
        console.warn('[continuum/dashboard]', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  const species = data?.species || [];
  const metrics = data?.metrics || { critical: 0, declining: 0, stable: 0, recovering: 0, total: 0 };
  const sources = data?.sources || [];
  const buildProgress = data?.buildProgress || { pct: 0, live: 0, total: 0 };

  const detailSpeciesObj = detailBinomial ? species.find(s => s.binomial === detailBinomial) : null;
  // Hardcoded "what" + "why" prose stays until Wave 2 — keyed by binomial.
  const detailExtra = detailBinomial ? SPECIES_DETAIL_BY_BINOMIAL[detailBinomial] : null;

  // Derived eco-header metric strip
  const ecoMetrics = [
    { val: metrics.critical,   label: 'Critical',   color: S.terra },
    { val: metrics.declining,  label: 'Declining',  color: S.amber },
    { val: metrics.stable,     label: 'Stable',     color: S.forest },
    { val: metrics.recovering, label: 'Recovering', color: S.water },
    { val: metrics.total > 0 ? `${metrics.total}` : '—', label: 'Tracked', color: S.mid },
  ];

  return (
    <div
      style={{
        height: '100%',
        background: S.bg,
        color: S.text,
        fontFamily: F.sans,
        fontSize: 14,
        fontWeight: 300,
        lineHeight: 1.65,
        display: 'grid',
        gridTemplateColumns: '220px 1fr',
        overflow: 'hidden',
      }}
    >
      {/* ───── SIDEBAR ───── */}
      <Sidebar
        intelligenceView={intelligenceView}
        onIntelligenceViewChange={setIntelligenceView}
      />

      {/* ───── MAIN ───── */}
      {/* Switches between the original Species Dashboard view and the
          three full-width Intelligence libraries. Sidebar drives this. */}
      {intelligenceView !== 'species' ? (
        <IntelligenceLibrary
          view={intelligenceView}
          workspaceId={workspaceId}
          species={species}
        />
      ) : (
      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gridTemplateRows:'auto 1fr auto', overflow:'hidden' }}>

        {/* Eco header */}
        <div style={{
          gridColumn:'1/-1', padding:'16px 24px',
          background:S.s1, borderBottom:`1px solid ${S.b1}`,
          display:'flex', alignItems:'center', gap:24,
        }}>
          <div>
            <div style={{ fontFamily:F.serif, fontSize:22, fontWeight:400, color:S.text, marginBottom:2, fontStyle:'italic' }}>
              Florida Everglades
            </div>
            <div style={{ fontSize:12, color:S.static }}>
              Subtropical wetland · 1.5M acres · South Florida · 20 Indicator Species Tracked
            </div>
          </div>
          <div style={{ display:'flex', gap:2, marginLeft:'auto' }}>
            {ecoMetrics.map((m, i) => (
              <div key={i} style={{
                padding:'10px 16px', background:S.s2, border:`1px solid ${S.b1}`,
                textAlign:'center', minWidth:100,
                borderRadius: i === 0 ? '8px 0 0 8px' : i === ecoMetrics.length-1 ? '0 8px 8px 0' : 0,
              }}>
                <div style={{ fontFamily:F.mono, fontSize:22, fontWeight:500, marginBottom:2, color:m.color }}>
                  {m.val}
                </div>
                <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.12em', textTransform:'uppercase', color:S.dim }}>
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Left panel — species grid */}
        <div style={{ overflowY:'auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>
          <SectionTitle>
            Indicator Species — {species.length} of 20 · Click any for detail
          </SectionTitle>
          {loading && species.length === 0 && (
            <div style={{ color:S.dim, fontSize:12, padding:'40px 0', textAlign:'center', fontStyle:'italic' }}>
              Loading species data…
            </div>
          )}
          {!loading && species.length === 0 && (
            <div style={{ color:S.dim, fontSize:12, padding:'40px 0', textAlign:'center', fontStyle:'italic' }}>
              No species data yet. Sync GBIF + eBird from the Resources tab.
            </div>
          )}
          {species.length > 0 && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {species.map(sp => (
                <SpeciesCard
                  key={sp.binomial}
                  sp={sp}
                  selected={selectedBinomial === sp.binomial}
                  onClick={() => { setSelectedBinomial(sp.binomial); setDetailBinomial(sp.binomial); }}
                />
              ))}
            </div>
          )}
          <div style={{ fontSize:11, color:S.dim, marginTop:4 }}>
            IUCN Status: CR=Critically Endangered · EN=Endangered · VU=Vulnerable · NT=Near Threatened · LC=Least Concern
            {' · '}? = no IUCN sync yet
          </div>
        </div>

        {/* Right panel — tabbed */}
        <div style={{ borderLeft:`1px solid ${S.b1}`, display:'flex', flexDirection:'column', overflow:'hidden', background:S.s1 }}>
          <div style={{ display:'flex', borderBottom:`1px solid ${S.b1}`, flexShrink:0 }}>
            {[
              { id:'cascade',      label:'Cascade' },
              { id:'story',        label:'Story' },
              { id:'intervention', label:'Intervention' },
            ].map(t => {
              const active = activeRpTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveRpTab(t.id)}
                  style={{
                    flex:1, padding:'10px 0',
                    fontFamily:F.mono, fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase',
                    cursor:'pointer', textAlign:'center',
                    color: active ? S.forest : S.static,
                    border:'none', background:'transparent',
                    borderBottom:`2px solid ${active ? S.forest : 'transparent'}`,
                    marginBottom:-1, transition:'all .15s',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.color = S.mid; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.color = S.static; }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:16 }}>
            {activeRpTab === 'cascade'      && <CascadePanel workspaceId={workspaceId} species={species} />}
            {activeRpTab === 'story'        && <StoryPanel workspaceId={workspaceId} species={species} />}
            {activeRpTab === 'intervention' && <InterventionPanel workspaceId={workspaceId} species={species} />}
          </div>
        </div>

        {/* Bottom panel — data source statuses */}
        <div style={{
          gridColumn:'1/-1', padding:'12px 24px',
          borderTop:`1px solid ${S.b1}`, background:S.s1,
          display:'flex', alignItems:'center', gap:20, flexShrink:0,
          flexWrap:'wrap',
        }}>
          {sources.map((src) => (
            <div key={src.source_key} style={{ display:'flex', alignItems:'center', gap:6, fontFamily:F.mono, fontSize:9, letterSpacing:'0.08em', color:S.dim }}>
              <div style={{
                width:5, height:5, borderRadius:'50%', flexShrink:0,
                background: src.status === 'live' ? S.forest : src.status === 'partial' ? S.amber : S.b3,
              }} />
              {src.name} — {src.record_count?.toLocaleString() || 0} records
              {src.status !== 'live' && src.status !== 'partial' && ' · pending'}
            </div>
          ))}
          <div style={{ marginLeft:'auto', fontFamily:F.mono, fontSize:9, color:S.dim }}>
            {(() => {
              const latest = sources
                .map(s => s.last_sync_at)
                .filter(Boolean)
                .sort()
                .pop();
              return latest
                ? `Last sync: ${new Date(latest).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                : '';
            })()}
          </div>
        </div>
      </div>
      )}

      {/* Detail modal — renders whenever a species is selected; the
          "extra" prose is optional and degrades gracefully. */}
      {detailSpeciesObj && (
        <DetailModal
          sp={detailSpeciesObj}
          extra={detailExtra}
          onClose={() => setDetailBinomial(null)}
        />
      )}
    </div>
  );
}

// ─── Sidebar ───────────────────────────────────────────────
// Pure navigation — Ecosystem (which biome) + Intelligence (which
// sub-view). Status info (data sources, build progress) lives in
// the Resources tab, not duplicated here.
function Sidebar({
  intelligenceView = 'species',
  onIntelligenceViewChange,
}) {
  const dotColor = (k) =>
    k === 'green' ? S.forest : k === 'amber' ? S.amber : k === 'red' ? S.terra : S.b3;

  return (
    <div style={{
      background:'rgba(10,16,11,.98)', borderRight:`1px solid ${S.b1}`,
      display:'flex', flexDirection:'column', overflow:'hidden', padding:'16px 0',
    }}>
      <SidebarSection label="Ecosystem">
        {ECOSYSTEMS.map(e => (
          <SidebarItem key={e.id} active={e.active}>
            <span style={{ width:6, height:6, borderRadius:'50%', flexShrink:0, background:dotColor(e.dot) }} />
            {e.label}
            {e.planned && <span style={{
              marginLeft:'auto', fontFamily:F.mono, fontSize:8, padding:'2px 6px', borderRadius:3,
              background:'rgba(200,148,62,.12)', color:S.amber,
            }}>planned</span>}
          </SidebarItem>
        ))}
      </SidebarSection>

      <SidebarSection label="Intelligence">
        {INTELLIGENCE.map(item => (
          <SidebarItem
            key={item.id}
            active={intelligenceView === item.id}
            onClick={() => onIntelligenceViewChange?.(item.id)}
            title={`Open ${item.label.replace(/^\W+\s*/, '')}`}
          >
            {item.label}
          </SidebarItem>
        ))}
      </SidebarSection>

    </div>
  );
}

function SidebarSection({ label, children }) {
  return (
    <div style={{ marginBottom:20 }}>
      <span style={{
        fontFamily:F.mono, fontSize:8, letterSpacing:'0.2em', textTransform:'uppercase',
        color:S.dim, padding:'0 16px 8px', display:'block',
      }}>
        {label}
      </span>
      {children}
    </div>
  );
}

function SidebarItem({ active, onClick, title, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        display:'flex', alignItems:'center', gap:9, padding:'8px 16px',
        cursor: onClick ? 'pointer' : 'default', transition:'all .14s', fontSize:13,
        color: active ? S.forest : S.static,
        border:'none', background: active ? 'rgba(74,140,92,.1)' : 'transparent',
        width:'100%', textAlign:'left', fontFamily:F.sans,
      }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = S.s2; e.currentTarget.style.color = S.mid; } }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = S.static; } }}
    >
      {children}
    </button>
  );
}

// ─── Section title ─────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <div style={{
      fontFamily:F.mono, fontSize:9, letterSpacing:'0.2em', textTransform:'uppercase',
      color:S.static, marginBottom:10,
      display:'flex', alignItems:'center', gap:10,
    }}>
      <span>{children}</span>
      <span style={{ flex:1, height:1, background:S.b1 }} />
    </div>
  );
}

// ─── Species card ──────────────────────────────────────────
function SpeciesCard({ sp, selected, onClick }) {
  const tierColor = sp.tier === 'critical' ? S.terra : sp.tier === 'watch' ? S.amber : sp.tier === 'recovering' ? S.water : S.forest;
  const sparkColor = sp.trendCls === 'down' ? S.terra : sp.trendCls === 'warn' ? S.amber : sp.trendCls === 'up' ? S.water : S.forest;
  const trendColor = sparkColor;
  const statusBg = {
    CR:'rgba(196,80,64,.15)', EN:'rgba(200,148,62,.12)', VU:'rgba(200,148,62,.08)',
    NT:'rgba(122,158,130,.1)', LC:'rgba(74,140,92,.1)',
  }[sp.status] || 'rgba(136,136,136,.08)';
  const statusFg = {
    CR:S.terra, EN:S.amber, VU:'#d4b060', NT:S.sage, LC:S.forest,
  }[sp.status] || S.dim;

  // 4 source dots: GBIF · eBird · IUCN · iNaturalist. Filled if synced.
  const SOURCE_DOTS = [
    { key:'G', label:'GBIF',        on: sp.hasGbif },
    { key:'E', label:'eBird',       on: sp.hasEbird },
    { key:'I', label:'IUCN',        on: sp.hasIucn },
    { key:'N', label:'iNaturalist', on: sp.hasInaturalist },
  ];

  const isLowConf = sp.confidence === 'low';

  return (
    <div
      onClick={onClick}
      style={{
        background:S.s2,
        border:`1px solid ${selected ? S.forest : S.b2}`,
        borderRadius:8, padding:'11px 13px',
        cursor:'pointer', transition:'all .15s',
        position:'relative', overflow:'hidden',
        boxShadow: 'none',
      }}
      onMouseEnter={e => {
        if (!selected) {
          e.currentTarget.style.borderColor = S.b3;
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 6px 16px rgba(0,0,0,.35)`;
        }
      }}
      onMouseLeave={e => {
        if (!selected) {
          e.currentTarget.style.borderColor = S.b2;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, background:tierColor }} />

      {/* Top row: name on left, kind tag on right */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8, marginBottom:1 }}>
        <div style={{ fontSize:13, fontWeight:500, color:S.mid, lineHeight:1.3 }}>
          {sp.common || sp.name}
        </div>
        {sp.kind && (
          <span style={{
            fontFamily:F.mono, fontSize:8, color:S.dim,
            letterSpacing:'0.12em', textTransform:'uppercase',
            flexShrink:0, marginTop:3,
          }}>
            {sp.kind}
          </span>
        )}
      </div>

      <div style={{ fontSize:11, color:S.static, marginBottom:8, fontStyle:'italic' }}>
        {sp.binomial || sp.common}
      </div>

      {/* Sparkline */}
      {sp.sparkPoints || sp.spark ? (
        <svg style={{ height:20, width:60, marginTop:0 }} viewBox="0 0 60 20">
          <polyline points={sp.sparkPoints || sp.spark} fill="none" stroke={sparkColor} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ) : (
        <div style={{ height:20, fontSize:9, color:S.dim, fontFamily:F.mono, fontStyle:'italic' }}>no series</div>
      )}

      {/* Source dots: G·E·I·N */}
      <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:6, fontFamily:F.mono, fontSize:9, letterSpacing:'0.08em' }}>
        {SOURCE_DOTS.map((d, i) => (
          <span key={d.key} title={`${d.label}: ${d.on ? 'synced' : 'not synced'}`}
            style={{ display:'flex', alignItems:'center', gap:0 }}>
            <span style={{
              color: d.on ? S.forest : S.b3,
              fontWeight: d.on ? 500 : 400,
            }}>
              {d.key}
            </span>
            {i < SOURCE_DOTS.length - 1 && (
              <span style={{ color:S.b3, marginLeft:4 }}>·</span>
            )}
          </span>
        ))}
      </div>

      {/* Bottom row: trend + period + low-conf flag (left), status (right) */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:6 }}>
        <div style={{ display:'flex', alignItems:'center', gap:4, fontFamily:F.mono, fontSize:10, fontWeight:500, color:trendColor }}>
          {sp.trendText || sp.trend}
          {sp.period && <span style={{ fontSize:9, color:S.static }}>{sp.period}</span>}
          {isLowConf && (
            <span title="Window too short to be authoritative — needs more data" style={{
              fontSize:8, color:S.dim, fontStyle:'italic', marginLeft:2,
            }}>
              · low conf
            </span>
          )}
        </div>
        <div style={{
          fontFamily:F.mono, fontSize:8, padding:'2px 7px', borderRadius:3,
          letterSpacing:'0.06em', textTransform:'uppercase',
          background:statusBg, color:statusFg,
          border: sp.status === '?' ? `1px dashed ${S.dim}` : 'none',
        }}>
          {sp.status === '?' ? 'pending' : sp.status}
        </div>
      </div>
    </div>
  );
}

// ─── Cascade panel ─────────────────────────────────────────
// Live cascade chains from continuum_cascades. Renders the most
// recent committed cascade as canonical; proposed cascades render
// above with a "Shift proposes" header + Accept ✓ / Dismiss ✕.
// "Draft Cascade" picker at the bottom lets the user generate
// a new one for any indicator species via Sonnet 4.6.
function CascadePanel({ workspaceId, species = [] }) {
  const [cascades, setCascades] = useState(null); // null = loading
  const [drafting, setDrafting] = useState(false);
  const [draftFor, setDraftFor] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/cascades?workspace_id=${workspaceId}`);
    if (r.ok) {
      const j = await r.json();
      setCascades(j.cascades || []);
    } else {
      setCascades([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  async function draft(triggerBinomial) {
    if (drafting) return;
    setDrafting(true);
    setDraftFor(triggerBinomial);
    try {
      await fetch('/api/continuum/propose-cascade', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, trigger_binomial: triggerBinomial }),
      });
      await reload();
    } catch { /* swallow — UI shows error via empty state if needed */ }
    setDrafting(false);
    setDraftFor(null);
  }

  async function accept(id) {
    await fetch('/api/continuum/cascades', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id, status: 'committed' }),
    });
    reload();
  }

  async function dismiss(id) {
    await fetch(`/api/continuum/cascades?id=${id}`, { method: 'DELETE' });
    reload();
  }

  if (cascades === null) {
    return <div style={{ fontSize:12, color:S.dim, fontStyle:'italic' }}>Loading…</div>;
  }

  // Sort: proposed first (so user sees pending action), then committed by recency
  const proposed = cascades.filter(c => c.status === 'proposed');
  const committed = cascades.filter(c => c.status === 'committed');
  const visible = [...proposed, ...committed];

  return (
    <div>
      {visible.length === 0 && (
        <EmptyCascade species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} />
      )}

      {visible.map((c) => (
        <CascadeCard
          key={c.id}
          cascade={c}
          onAccept={() => accept(c.id)}
          onDismiss={() => dismiss(c.id)}
        />
      ))}

      {/* Always-available draft picker at the bottom (when cascades exist) */}
      {visible.length > 0 && (
        <DraftPicker species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} compact />
      )}
    </div>
  );
}

function EmptyCascade({ species, drafting, draftFor, onDraft }) {
  return (
    <div>
      <div style={{ fontSize:13, color:S.static, lineHeight:1.65, marginBottom:14 }}>
        No cascade yet. Pick a trigger species and Shift will draft a 5-node chain
        from the real species data — what happens if that species disappears.
        You can accept, dismiss, or refine via chat.
      </div>
      <DraftPicker species={species} drafting={drafting} draftFor={draftFor} onDraft={onDraft} />
    </div>
  );
}

function DraftPicker({ species, drafting, draftFor, onDraft, compact = false, label, accent = S.gold }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const buttonLabel = label || '+ Draft another cascade';

  if (compact && !pickerOpen) {
    return (
      <button
        onClick={() => setPickerOpen(true)}
        disabled={drafting}
        style={{
          marginTop:14, width:'100%',
          background:'transparent', border:`1px dashed ${S.b3}`,
          borderRadius:6, padding:'8px 12px',
          color:S.static, fontFamily:F.mono, fontSize:10,
          letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.color = accent; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = S.b3; e.currentTarget.style.color = S.static; }}
      >
        {buttonLabel}
      </button>
    );
  }

  return (
    <div style={{
      marginTop: compact ? 14 : 0,
      padding:'12px 14px',
      background:S.s2, border:`1px solid ${S.b2}`, borderRadius:8,
    }}>
      <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em', textTransform:'uppercase', color:accent, marginBottom:8 }}>
        Draft from a trigger species
      </div>
      <div style={{ fontSize:12, color:S.static, lineHeight:1.6, marginBottom:10 }}>
        Pick a trigger species. Shift drafts from the real data.
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
        {(species || []).map(sp => {
          const isThisDrafting = drafting && draftFor === sp.binomial;
          return (
            <button
              key={sp.binomial}
              onClick={() => onDraft(sp.binomial)}
              disabled={drafting}
              style={{
                background: isThisDrafting ? `${accent}20` : 'transparent',
                border: `1px solid ${isThisDrafting ? accent : S.b2}`,
                borderRadius:5, padding:'6px 10px',
                fontFamily:F.sans, fontSize:11,
                color: isThisDrafting ? accent : S.mid,
                cursor: drafting ? 'wait' : 'pointer',
                textAlign:'left', transition:'all .15s',
              }}
              onMouseEnter={e => { if (!drafting) { e.currentTarget.style.borderColor = accent; e.currentTarget.style.color = accent; } }}
              onMouseLeave={e => { if (!drafting) { e.currentTarget.style.borderColor = S.b2; e.currentTarget.style.color = S.mid; } }}
            >
              {isThisDrafting ? '⟳ ' : ''}{sp.common}
            </button>
          );
        })}
      </div>
      {compact && (
        <div style={{ marginTop:10, textAlign:'right' }}>
          <button
            onClick={() => setPickerOpen(false)}
            disabled={drafting}
            style={{
              background:'transparent', border:'none',
              color:S.dim, fontFamily:F.mono, fontSize:9, letterSpacing:'0.06em',
              cursor:'pointer', textTransform:'uppercase',
            }}
          >
            cancel
          </button>
        </div>
      )}
    </div>
  );
}

function CascadeCard({ cascade, onAccept, onDismiss }) {
  const isProposed = cascade.status === 'proposed';
  const triggerName = cascade.trigger_common || 'a species';

  return (
    <div style={{ marginBottom: 16 }}>
      {isProposed && (
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'8px 12px', marginBottom:8,
          background:'rgba(200,169,110,.06)',
          border:`1px solid ${S.gold}40`,
          borderRadius:6,
        }}>
          <div style={{
            fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em', textTransform:'uppercase',
            color:S.gold, fontWeight:500,
          }}>
            ◈ Shift proposes
          </div>
          <div style={{ flex:1 }} />
          <button
            onClick={onDismiss}
            style={{
              fontFamily:F.mono, fontSize:9, padding:'3px 10px', borderRadius:4,
              background:'transparent', border:`1px solid ${S.b2}`, color:S.static,
              cursor:'pointer', letterSpacing:'0.06em',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = S.terra; e.currentTarget.style.color = S.terra; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = S.b2; e.currentTarget.style.color = S.static; }}
          >
            ✕ DISMISS
          </button>
          <button
            onClick={onAccept}
            style={{
              fontFamily:F.mono, fontSize:9, padding:'3px 10px', borderRadius:4,
              background:`${S.forest}20`, border:`1px solid ${S.forest}`, color:S.forest,
              cursor:'pointer', letterSpacing:'0.06em', fontWeight:500,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${S.forest}30`; }}
            onMouseLeave={e => { e.currentTarget.style.background = `${S.forest}20`; }}
          >
            ✓ ACCEPT
          </button>
        </div>
      )}

      <div style={{ fontSize:13, color:S.static, lineHeight:1.65, marginBottom:14 }}>
        <strong style={{ color:S.mid, fontWeight:500 }}>{cascade.name || `If the ${triggerName} disappears`}</strong>{' '}
        — here is what follows. Each link is grounded in the real species data.{' '}
        {!isProposed && cascade.source === 'shift' && (
          <em style={{ color:S.amber, fontStyle:'normal', fontSize:11 }}>(drafted by Shift, accepted)</em>
        )}
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
        {(cascade.nodes || []).map((node, i) => (
          <div key={i}>
            <CascadeNode node={node} />
            {i < (cascade.nodes || []).length - 1 && (
              <div style={{ margin:'2px 0 6px 20px', fontFamily:F.mono, fontSize:9, color:S.dim }}>↓ causes</div>
            )}
          </div>
        ))}
      </div>

      {cascade.data_sources && (
        <div style={{
          marginTop:14, padding:'10px 12px',
          background:'rgba(196,80,64,.05)', border:'1px solid rgba(196,80,64,.2)',
          borderRadius:6,
        }}>
          <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em', textTransform:'uppercase', color:S.terra, marginBottom:5 }}>
            Data Sources for This Cascade
          </div>
          <div style={{ fontSize:11, color:S.dim, lineHeight:1.6 }}>
            {cascade.data_sources}
          </div>
        </div>
      )}
    </div>
  );
}

function CascadeNode({ node }) {
  const palette = {
    trigger: { border:'rgba(196,80,64,.35)', bg:'rgba(196,80,64,.05)', dot:{bg:'rgba(196,80,64,.2)', fg:S.terra} },
    level1:  { border:'rgba(200,148,62,.25)', bg:'rgba(200,148,62,.04)', dot:{bg:'rgba(200,148,62,.18)', fg:S.amber} },
    level2:  { border:'rgba(200,148,62,.15)', bg:'transparent',          dot:{bg:'rgba(200,148,62,.1)',  fg:'#d4b060'} },
    impact:  { border:'rgba(61,138,122,.25)', bg:'rgba(61,138,122,.04)', dot:{bg:'rgba(61,138,122,.18)', fg:S.water} },
  }[node.cls] || { border:S.b2, bg:S.s2, dot:{bg:S.b2, fg:S.static} };

  return (
    <div style={{
      display:'flex', alignItems:'flex-start', gap:10,
      padding:'11px 13px',
      background: palette.bg === 'transparent' ? S.s2 : palette.bg,
      border:`1px solid ${palette.border}`, borderRadius:8, marginBottom:4,
      cursor:'pointer', transition:'all .15s',
    }}>
      <div style={{
        flexShrink:0, width:14, height:14, borderRadius:'50%',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontFamily:F.mono, fontSize:8, marginTop:1,
        background:palette.dot.bg, color:palette.dot.fg,
      }}>
        {node.level}
      </div>
      <div>
        <div style={{ fontSize:13, fontWeight:500, color:S.mid, marginBottom:2 }}>{node.species}</div>
        <div style={{ fontSize:11, color:S.static, lineHeight:1.5 }}>{node.reason}</div>
      </div>
    </div>
  );
}

// ─── Story panel ───────────────────────────────────────────
// Live narratives from continuum_stories. Same Wave 2 pattern as
// CascadePanel: ghost row for proposed → Accept/Dismiss → committed.
// Stories also have an "is_verified" flag — drafts are always
// unverified until the user confirms a real interview was conducted.
function StoryPanel({ workspaceId, species = [] }) {
  const [stories, setStories] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftFor, setDraftFor] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/stories?workspace_id=${workspaceId}`);
    if (r.ok) {
      const j = await r.json();
      setStories(j.stories || []);
    } else {
      setStories([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  async function draft(triggerBinomial) {
    if (drafting) return;
    setDrafting(true);
    setDraftFor(triggerBinomial);
    try {
      await fetch('/api/continuum/propose-story', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, trigger_binomial: triggerBinomial }),
      });
      await reload();
    } catch { /* swallow */ }
    setDrafting(false);
    setDraftFor(null);
  }

  async function accept(id) {
    await fetch('/api/continuum/stories', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id, status: 'committed' }),
    });
    reload();
  }

  async function dismiss(id) {
    await fetch(`/api/continuum/stories?id=${id}`, { method: 'DELETE' });
    reload();
  }

  async function markVerified(id) {
    await fetch('/api/continuum/stories', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id, is_verified: true }),
    });
    reload();
  }

  if (stories === null) {
    return <div style={{ fontSize:12, color:S.dim, fontStyle:'italic' }}>Loading…</div>;
  }

  const proposed  = stories.filter(s => s.status === 'proposed');
  const committed = stories.filter(s => s.status === 'committed');
  const visible   = [...proposed, ...committed];

  return (
    <div>
      {visible.length === 0 && (
        <EmptyStory species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} />
      )}

      {visible.map(st => (
        <StoryCard
          key={st.id}
          story={st}
          onAccept={() => accept(st.id)}
          onDismiss={() => dismiss(st.id)}
          onMarkVerified={() => markVerified(st.id)}
        />
      ))}

      {visible.length > 0 && (
        <DraftPicker
          species={species}
          drafting={drafting}
          draftFor={draftFor}
          onDraft={draft}
          compact
          label="+ Draft another story"
          accent={S.water}
        />
      )}
    </div>
  );
}

function EmptyStory({ species, drafting, draftFor, onDraft }) {
  return (
    <div>
      <div style={{ fontSize:13, color:S.static, lineHeight:1.65, marginBottom:14 }}>
        No story yet. Pick a trigger species and Shift will draft a 4-paragraph
        narrative — a person, what they noticed, the mechanism, the present
        consequence — grounded in the real species data. Always marked DRAFT
        until you verify with a real interview.
      </div>
      <DraftPicker
        species={species}
        drafting={drafting}
        draftFor={draftFor}
        onDraft={onDraft}
        accent={S.water}
      />
    </div>
  );
}

function StoryCard({ story, onAccept, onDismiss, onMarkVerified }) {
  const isProposed = story.status === 'proposed';
  const headlineText = story.headline || 'Untitled Story';
  const personLine = story.person_name
    ? `${story.person_name}${story.person_context ? ` — ${story.person_context}` : ''}`
    : null;

  return (
    <div style={{ marginBottom:16 }}>
      {isProposed && (
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'8px 12px', marginBottom:8,
          background:'rgba(155,143,196,.06)',
          border:`1px solid ${S.water}40`,
          borderRadius:6,
        }}>
          <div style={{
            fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em', textTransform:'uppercase',
            color:S.water, fontWeight:500,
          }}>
            ◈ Shift proposes
          </div>
          <div style={{ flex:1 }} />
          <button
            onClick={onDismiss}
            style={{
              fontFamily:F.mono, fontSize:9, padding:'3px 10px', borderRadius:4,
              background:'transparent', border:`1px solid ${S.b2}`, color:S.static,
              cursor:'pointer', letterSpacing:'0.06em',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = S.terra; e.currentTarget.style.color = S.terra; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = S.b2; e.currentTarget.style.color = S.static; }}
          >
            ✕ DISMISS
          </button>
          <button
            onClick={onAccept}
            style={{
              fontFamily:F.mono, fontSize:9, padding:'3px 10px', borderRadius:4,
              background:`${S.forest}20`, border:`1px solid ${S.forest}`, color:S.forest,
              cursor:'pointer', letterSpacing:'0.06em', fontWeight:500,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${S.forest}30`; }}
            onMouseLeave={e => { e.currentTarget.style.background = `${S.forest}20`; }}
          >
            ✓ ACCEPT
          </button>
        </div>
      )}

      <div style={{
        background:S.s2, border:`1px solid ${S.b2}`,
        borderRadius:8, overflow:'hidden', marginBottom:10,
      }}>
        <div style={{ height:3, background:`linear-gradient(90deg, ${S.water}, ${S.forest} 60%, transparent)` }} />
        <div style={{ padding:'14px 15px' }}>
          <div style={{
            display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap',
          }}>
            <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase', color:S.water }}>
              {story.eyebrow || `The Story · ${story.ecosystem || 'Florida Everglades'}`}
            </div>
            {!story.is_verified && (
              <span style={{
                fontFamily:F.mono, fontSize:8, padding:'1px 6px', borderRadius:3,
                background:`${S.gold}15`, color:S.gold, border:`1px solid ${S.gold}40`,
                letterSpacing:'0.08em',
              }}>
                DRAFT
              </span>
            )}
            {story.is_verified && (
              <span style={{
                fontFamily:F.mono, fontSize:8, padding:'1px 6px', borderRadius:3,
                background:`${S.forest}15`, color:S.forest, border:`1px solid ${S.forest}40`,
                letterSpacing:'0.08em',
              }}>
                VERIFIED
              </span>
            )}
          </div>
          <div style={{ fontFamily:F.serif, fontSize:18, fontWeight:400, color:S.text, lineHeight:1.4, marginBottom:6 }}>
            {headlineText}
          </div>
          {personLine && (
            <div style={{ fontFamily:F.sans, fontSize:11, color:S.static, fontStyle:'italic', marginBottom:14 }}>
              {personLine}
            </div>
          )}
          <div style={{ fontSize:13, color:S.static, lineHeight:1.75 }}>
            {(story.paragraphs || []).map((p, i, arr) => (
              <p key={i} style={{ marginBottom: i === arr.length - 1 ? 0 : 12 }}>{p}</p>
            ))}
          </div>
        </div>
      </div>

      {story.caveat && (
        <div style={{ fontSize:11, color:S.dim, padding:'4px 0', lineHeight:1.55 }}>
          {story.caveat}
        </div>
      )}

      {!isProposed && !story.is_verified && (
        <div style={{ marginTop:8, textAlign:'right' }}>
          <button
            onClick={onMarkVerified}
            title="Mark this story as verified after conducting a real interview"
            style={{
              fontFamily:F.mono, fontSize:9, padding:'4px 12px', borderRadius:4,
              background:`${S.forest}10`, border:`1px solid ${S.forest}`,
              color:S.forest, cursor:'pointer', letterSpacing:'0.06em', textTransform:'uppercase',
            }}
            onMouseEnter={e => e.currentTarget.style.background = `${S.forest}25`}
            onMouseLeave={e => e.currentTarget.style.background = `${S.forest}10`}
          >
            ✓ Mark verified
          </button>
        </div>
      )}

      {story.data_sources && (
        <div style={{
          marginTop:12, padding:'10px 12px',
          background:'rgba(61,138,122,.05)', border:'1px solid rgba(61,138,122,.2)',
          borderRadius:6,
        }}>
          <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em', textTransform:'uppercase', color:S.water, marginBottom:5 }}>
            Data Sources for This Story
          </div>
          <div style={{ fontSize:11, color:S.dim, lineHeight:1.6 }}>
            {story.data_sources}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Intervention panel ────────────────────────────────────
// Live costed-action proposals from continuum_interventions. Same
// Wave 2 pattern as cascade + story: ghost row → Accept/Dismiss →
// committed canonical card.
function InterventionPanel({ workspaceId, species = [] }) {
  const [interventions, setInterventions] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftFor, setDraftFor] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/interventions?workspace_id=${workspaceId}&context_type=florida_indicator,manual`);
    if (r.ok) {
      const j = await r.json();
      setInterventions(j.interventions || []);
    } else {
      setInterventions([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  async function draft(triggerBinomial) {
    if (drafting) return;
    setDrafting(true);
    setDraftFor(triggerBinomial);
    try {
      await fetch('/api/continuum/propose-intervention', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, trigger_binomial: triggerBinomial }),
      });
      await reload();
    } catch { /* swallow */ }
    setDrafting(false);
    setDraftFor(null);
  }

  async function accept(id) {
    await fetch('/api/continuum/interventions', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id, status: 'committed' }),
    });
    reload();
  }

  async function dismiss(id) {
    await fetch(`/api/continuum/interventions?id=${id}`, { method: 'DELETE' });
    reload();
  }

  if (interventions === null) {
    return <div style={{ fontSize:12, color:S.dim, fontStyle:'italic' }}>Loading…</div>;
  }

  const proposed  = interventions.filter(i => i.status === 'proposed');
  const committed = interventions.filter(i => i.status === 'committed');
  const visible   = [...proposed, ...committed];

  return (
    <div>
      {visible.length === 0 && (
        <EmptyIntervention species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} />
      )}

      {visible.map(iv => (
        <InterventionCard
          key={iv.id}
          intervention={iv}
          onAccept={() => accept(iv.id)}
          onDismiss={() => dismiss(iv.id)}
        />
      ))}

      {visible.length > 0 && (
        <DraftPicker
          species={species}
          drafting={drafting}
          draftFor={draftFor}
          onDraft={draft}
          compact
          label="+ Draft another intervention"
          accent={S.forest}
        />
      )}
    </div>
  );
}

function EmptyIntervention({ species, drafting, draftFor, onDraft }) {
  return (
    <div>
      <div style={{ fontSize:13, color:S.static, lineHeight:1.65, marginBottom:14 }}>
        No intervention yet. Pick a trigger species and Shift will draft a
        costed proposal — title, dollar cost, 5 outcomes, action window —
        designed to arrest the cascade for that species. Grounded in
        Conservation Evidence + USFWS recovery plans.
      </div>
      <DraftPicker
        species={species}
        drafting={drafting}
        draftFor={draftFor}
        onDraft={onDraft}
        accent={S.forest}
      />
    </div>
  );
}

function InterventionCard({ intervention, onAccept, onDismiss }) {
  const isProposed = intervention.status === 'proposed';

  return (
    <div style={{ marginBottom:16 }}>
      {isProposed && (
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'8px 12px', marginBottom:8,
          background:'rgba(74,140,92,.06)',
          border:`1px solid ${S.forest}40`,
          borderRadius:6,
        }}>
          <div style={{
            fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em', textTransform:'uppercase',
            color:S.forest, fontWeight:500,
          }}>
            ◈ Shift proposes
          </div>
          <div style={{ flex:1 }} />
          <button
            onClick={onDismiss}
            style={{
              fontFamily:F.mono, fontSize:9, padding:'3px 10px', borderRadius:4,
              background:'transparent', border:`1px solid ${S.b2}`, color:S.static,
              cursor:'pointer', letterSpacing:'0.06em',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = S.terra; e.currentTarget.style.color = S.terra; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = S.b2; e.currentTarget.style.color = S.static; }}
          >
            ✕ DISMISS
          </button>
          <button
            onClick={onAccept}
            style={{
              fontFamily:F.mono, fontSize:9, padding:'3px 10px', borderRadius:4,
              background:`${S.forest}20`, border:`1px solid ${S.forest}`, color:S.forest,
              cursor:'pointer', letterSpacing:'0.06em', fontWeight:500,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${S.forest}30`; }}
            onMouseLeave={e => { e.currentTarget.style.background = `${S.forest}20`; }}
          >
            ✓ ACCEPT
          </button>
        </div>
      )}

      <div style={{
        background:S.s2, border:'1px solid rgba(74,140,92,.25)',
        borderRadius:8, overflow:'hidden', marginBottom:10,
      }}>
        <div style={{ height:3, background:`linear-gradient(90deg, ${S.forest}, ${S.sage} 60%, transparent)` }} />
        <div style={{ padding:'14px 15px' }}>
          <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase', color:S.forest, marginBottom:8 }}>
            {intervention.label || `Intervention · ${intervention.ecosystem || 'Florida Everglades'}`}
          </div>
          <div style={{ fontFamily:F.serif, fontSize:15, color:S.text, marginBottom:10, lineHeight:1.4 }}>
            {intervention.title}
          </div>
          {intervention.cost_display && (
            <div style={{
              background:'rgba(74,140,92,.08)', border:'1px solid rgba(74,140,92,.2)',
              borderRadius:6, padding:'10px 12px', marginBottom:10,
              display:'flex', alignItems:'center', gap:14,
            }}>
              <div style={{ fontFamily:F.mono, fontSize:24, fontWeight:500, color:S.forest, flexShrink:0 }}>
                {intervention.cost_display}
              </div>
              {intervention.cost_description && (
                <div style={{ fontSize:12, color:S.static, lineHeight:1.5 }}>
                  {intervention.cost_description}
                </div>
              )}
            </div>
          )}
          {Array.isArray(intervention.outcomes) && intervention.outcomes.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
              {intervention.outcomes.map((o, i) => (
                <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:12, color:S.static, lineHeight:1.5 }}>
                  <div style={{ width:5, height:5, borderRadius:'50%', background:S.forest, flexShrink:0, marginTop:5 }} />
                  {o}
                </div>
              ))}
            </div>
          )}
          {intervention.window_text && (
            <>
              <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.1em', textTransform:'uppercase', color:S.dim, marginBottom:6 }}>
                Intervention Window
              </div>
              <div style={{
                background:'rgba(200,148,62,.08)', border:'1px solid rgba(200,148,62,.2)',
                borderRadius:5, padding:'8px 10px', fontSize:12, color:S.static, marginBottom:0,
              }}>
                {intervention.window_text}
              </div>
            </>
          )}
        </div>
      </div>
      {intervention.data_sources && (
        <div style={{ fontSize:11, color:S.dim, padding:'4px 0', lineHeight:1.6 }}>
          {intervention.data_sources}
        </div>
      )}
    </div>
  );
}

// ─── Detail modal ──────────────────────────────────────────
function DetailModal({ sp, extra, onClose }) {
  const [lightbox, setLightbox] = useState(false);
  // Real GBIF series, fallback to legacy mock array if missing.
  const series = sp?.gbifSeries
    ? Object.entries(sp.gbifSeries).map(([y, v]) => ({ year: Number(y), value: Number(v) })).sort((a, b) => a.year - b.year)
    : (extra?.trend || []).map((v, i) => ({ year: 2014 + i, value: v }));
  const max = Math.max(1, ...series.map(s => s.value));
  const seriesColor = extra?.color || S.forest;
  // Status eyebrow: when IUCN has synced, use the REAL category code
  // expanded to its full name (CR → Critically Endangered, etc).
  // When no IUCN, neutral "Indicator Species".
  const IUCN_FULL = {
    CR: 'Critically Endangered', EN: 'Endangered', VU: 'Vulnerable',
    NT: 'Near Threatened', LC: 'Least Concern', DD: 'Data Deficient',
    EX: 'Extinct', EW: 'Extinct in the Wild',
  };
  const statusFull = sp?.hasIucn
    ? (IUCN_FULL[sp.status] || sp.status || 'Indicator Species')
    : 'Indicator Species';

  // IUCN assessment age: 0-2 fresh, 3-5 aging, 6+ stale.
  const ageStr   = sp?.iucnAge != null ? `${sp.iucnAge}yr ago` : null;
  const ageCls   = sp?.iucnAge == null ? null
                 : sp.iucnAge >= 6 ? 'stale'
                 : sp.iucnAge >= 3 ? 'aging' : 'fresh';
  const ageColor = ageCls === 'stale' ? S.terra
                 : ageCls === 'aging' ? S.amber
                 : S.forest;

  // Build the dynamic data-sources line from actually-synced sources
  // (replaces the legacy hardcoded SPECIES_DETAIL.sources prose).
  const dynamicSources = [];
  if (sp?.hasGbif)        dynamicSources.push('GBIF (Florida bbox observations, 2014–2024)');
  if (sp?.hasEbird)       dynamicSources.push('eBird (last 30 days, US-FL region)');
  if (sp?.hasIucn)        dynamicSources.push(`IUCN Red List${sp.iucnYear ? ` ${sp.iucnYear}` : ''} assessment`);
  if (sp?.hasInaturalist) dynamicSources.push('iNaturalist (taxonomy + identification photo)');
  const what = extra?.what || '';
  const why  = extra?.why  || '';
  const sources = extra?.sources || '';
  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, background:'rgba(7,16,10,.85)',
        zIndex:100, display:'flex', alignItems:'center', justifyContent:'center',
        backdropFilter:'blur(4px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background:S.s2, border:`1px solid ${S.b2}`,
          borderRadius:12, padding:24, width:560,
          maxHeight:'85vh', overflowY:'auto',
          position:'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position:'absolute', top:14, right:14,
            background:'rgba(7,16,10,.6)', border:'none', color:S.text,
            cursor:'pointer', fontSize:14, width:28, height:28,
            borderRadius:'50%', zIndex:2,
            display:'flex', alignItems:'center', justifyContent:'center',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(196,80,64,.5)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(7,16,10,.6)'}
        >✕</button>

        {/* ── Hero strip: 240px image (or placeholder) + identification ─ */}
        <div style={{ display:'flex', gap:16, marginBottom:18 }}>
          {sp?.imageUrl ? (
            <div
              onClick={() => setLightbox(true)}
              title="Click to enlarge"
              style={{
                width:240, height:240, flexShrink:0,
                borderRadius:8, border:`1px solid ${S.b2}`,
                background:S.s3, position:'relative', overflow:'hidden',
                cursor:'zoom-in',
              }}
            >
              <img
                src={sp.imageUrl}
                alt={sp.common || sp.binomial}
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div style={{
                position:'absolute', bottom:6, right:6,
                background:'rgba(7,16,10,.7)', color:S.text,
                fontSize:9, fontFamily:F.mono, padding:'3px 7px',
                borderRadius:3, letterSpacing:'0.06em',
                pointerEvents:'none',
              }}>
                ⤢ enlarge
              </div>
            </div>
          ) : (
            <div style={{
              width:240, height:240, flexShrink:0, borderRadius:8,
              background:S.s3, border:`1px dashed ${S.b3}`,
              display:'flex', alignItems:'center', justifyContent:'center',
              color:S.dim, fontSize:11, fontFamily:F.mono, textAlign:'center', padding:12,
            }}>
              No photo yet — sync iNaturalist
            </div>
          )}

          <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column' }}>
            <div style={{
              fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em',
              textTransform:'uppercase', color:S.forest, marginBottom:6,
              display:'flex', alignItems:'center', gap:8, flexWrap:'wrap',
            }}>
              <span>{statusFull}</span>
              {sp?.hasIucn && sp?.iucnYear && (
                <>
                  <span style={{ color:S.dim }}>·</span>
                  <span style={{ color: ageColor }}>
                    IUCN {sp.iucnYear}{ageStr ? ` (${ageStr})` : ''}
                  </span>
                </>
              )}
              {!sp?.hasIucn && (
                <>
                  <span style={{ color:S.dim }}>·</span>
                  <span style={{ color:S.static }}>Awaiting IUCN sync</span>
                </>
              )}
              {ageCls === 'stale' && (
                <span title="IUCN reassesses on a 5-year cycle — this assessment may be outdated" style={{
                  fontSize:8, padding:'1px 6px', borderRadius:3,
                  background:`${S.terra}15`, color:S.terra, border:`1px solid ${S.terra}40`,
                  letterSpacing:'0.06em',
                }}>
                  STALE
                </span>
              )}
            </div>
            <div style={{
              fontFamily:F.serif, fontSize:22, fontWeight:400,
              color:S.text, marginBottom:4, lineHeight:1.2,
            }}>
              {sp?.common || sp?.name}
            </div>
            <div style={{ fontSize:12, color:S.static, fontStyle:'italic', marginBottom:14 }}>
              {sp?.binomial}
            </div>

            {/* Status badge — only render the colored CR/EN/VU/etc badge
                when IUCN has actually synced. Until then, show a neutral
                "pending IUCN" pill so we don't fabricate a wrong status. */}
            {sp?.hasIucn && sp?.status && sp.status !== '?' && (
              <div style={{
                alignSelf:'flex-start',
                fontFamily:F.mono, fontSize:9, padding:'3px 9px', borderRadius:3,
                letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:10,
                background: sp.tier === 'critical' ? 'rgba(196,80,64,.15)'
                  : sp.tier === 'watch' ? 'rgba(200,148,62,.12)'
                  : sp.tier === 'recovering' ? 'rgba(61,138,122,.12)'
                  : 'rgba(74,140,92,.1)',
                color: sp.tier === 'critical' ? S.terra
                  : sp.tier === 'watch' ? S.amber
                  : sp.tier === 'recovering' ? S.water
                  : S.forest,
              }}>
                {sp.status}
              </div>
            )}
            {!sp?.hasIucn && (
              <div style={{
                alignSelf:'flex-start',
                fontFamily:F.mono, fontSize:9, padding:'3px 9px', borderRadius:3,
                letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:10,
                background:`${S.dim}20`, color:S.static,
                border:`1px dashed ${S.dim}`,
              }}>
                Pending IUCN
              </div>
            )}

            {sp?.imageAttribution && (
              <div style={{ marginTop:'auto', fontSize:9, color:S.dim, fontStyle:'italic', lineHeight:1.4 }}>
                Photo: <span dangerouslySetInnerHTML={{ __html: sp.imageAttribution }} />
                {sp.imageLicense && <> · {sp.imageLicense}</>}
              </div>
            )}
          </div>
        </div>

        <DmSection>
          {sp?.gbifSeries
            ? `GBIF Occurrences — ${series.length} years`
            : 'Population Trend (legacy mock data)'}
        </DmSection>
        {series.length > 0 ? (
          <>
            <div style={{
              height:80, background:S.s3, border:`1px solid ${S.b1}`, borderRadius:6,
              display:'flex', alignItems:'flex-end', padding:6, gap:3, overflow:'hidden', marginBottom:6,
            }}>
              {series.map(({ year, value }, i) => {
                const h = Math.max(3, Math.round((value / max) * 60));
                const clr = sp?.gbifSeries
                  ? seriesColor
                  : value < 50 ? S.terra : value < 75 ? S.amber : seriesColor;
                return (
                  <div key={year} title={`${year}: ${value}`} style={{
                    flex:1, borderRadius:'2px 2px 0 0',
                    minHeight:3, height:h, background:clr,
                    opacity: 0.6 + i * (0.4 / Math.max(1, series.length)),
                    transition:'height .3s',
                  }} />
                );
              })}
            </div>
            {sp?.gbifSeries && (
              <div style={{ fontSize:10, color:S.dim, fontStyle:'italic', marginBottom:10, lineHeight:1.55 }}>
                ⓘ GBIF observation reports per year — not a population estimate.
                Reflects how often the species was reported, not how many individuals exist.
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize:11, color:S.dim, fontStyle:'italic', marginBottom:10 }}>
            No occurrence data yet. Run GBIF sync from the Resources tab.
          </div>
        )}

        {sp?.hasEbird && sp?.ebirdSightings != null && (
          <>
            <DmSection>eBird Sightings — last 30 days, Florida</DmSection>
            <div style={{ fontSize:13, color:S.static, lineHeight:1.7 }}>
              {sp.ebirdSightings.toLocaleString()} sightings
              {sp.ebirdTotal != null && ` · ${sp.ebirdTotal.toLocaleString()} individuals counted`}
            </div>
          </>
        )}

        {what && (
          <>
            <DmSection>What This Species Does</DmSection>
            <div style={{ fontSize:13, color:S.static, lineHeight:1.7 }}>{what}</div>
          </>
        )}

        {why && (
          <>
            <DmSection>Why It Matters to This Cascade</DmSection>
            <div style={{
              background:'rgba(200,148,62,.05)', border:'1px solid rgba(200,148,62,.2)',
              borderLeft:`3px solid ${S.amber}`, borderRadius:'0 6px 6px 0',
              padding:'10px 12px', fontSize:13, color:S.static, lineHeight:1.65, marginTop:10,
            }}>
              {why}
            </div>
          </>
        )}

        {/* Data sources: dynamic from actually-synced sources, with the
            legacy hardcoded prose as a fallback when nothing is wired yet. */}
        {dynamicSources.length > 0 ? (
          <>
            <DmSection>Data Sources (live)</DmSection>
            <ul style={{ fontSize:11, color:S.dim, lineHeight:1.7, marginTop:4, paddingLeft:18 }}>
              {dynamicSources.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </>
        ) : sources && (
          <>
            <DmSection>Data Sources</DmSection>
            <div style={{ fontSize:11, color:S.dim, lineHeight:1.7 }}>{sources}</div>
          </>
        )}
      </div>

      {/* ── Lightbox: full-size species photo ───────────── */}
      {lightbox && sp?.imageUrl && (
        <div
          onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
          style={{
            position:'fixed', inset:0,
            background:'rgba(7,16,10,.96)',
            zIndex:200, display:'flex',
            alignItems:'center', justifyContent:'center',
            padding:40, cursor:'zoom-out',
          }}
        >
          <img
            src={sp.imageLargeUrl || sp.imageUrl}
            alt={sp.common || sp.binomial}
            style={{
              maxWidth:'100%', maxHeight:'100%',
              objectFit:'contain', borderRadius:6,
              boxShadow:'0 20px 60px rgba(0,0,0,.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            style={{
              position:'absolute', top:20, right:20,
              background:'rgba(7,16,10,.6)', border:'none', color:S.text,
              cursor:'pointer', fontSize:18, width:36, height:36,
              borderRadius:'50%', zIndex:1,
              display:'flex', alignItems:'center', justifyContent:'center',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(196,80,64,.5)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(7,16,10,.6)'}
          >✕</button>
          {sp.imageAttribution && (
            <div
              style={{
                position:'absolute', bottom:20, left:20, right:20,
                fontSize:10, color:S.mid, fontFamily:F.mono,
                fontStyle:'italic', textAlign:'center', opacity:0.7,
              }}
              onClick={(e) => e.stopPropagation()}
              dangerouslySetInnerHTML={{ __html: `Photo: ${sp.imageAttribution}${sp.imageLicense ? ` · ${sp.imageLicense}` : ''}` }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// INTELLIGENCE LIBRARIES — full-width browsers for cascades /
// stories / interventions. Selected via the sidebar Intelligence
// section. Reuse the existing Card components but in a wider
// single-column layout with a filter strip + summary header.
// ═══════════════════════════════════════════════════════════

function IntelligenceLibrary({ view, workspaceId, species }) {
  if (view === 'cascades')      return <CascadeLibrary      workspaceId={workspaceId} species={species} />;
  if (view === 'stories')       return <StoryLibrary        workspaceId={workspaceId} species={species} />;
  if (view === 'interventions') return <InterventionLibrary workspaceId={workspaceId} species={species} />;
  return null;
}

// ── Generic library shell — header + filter tabs + scrollable body ──
function LibraryShell({ title, subtitle, accent, count, filterTabs, activeFilter, onFilterChange, children }) {
  return (
    <div style={{
      display:'flex', flexDirection:'column', overflow:'hidden',
      background:S.bg,
    }}>
      {/* Header */}
      <div style={{
        padding:'18px 28px',
        background:S.s1, borderBottom:`1px solid ${S.b1}`,
        flexShrink:0,
      }}>
        <div style={{
          display:'flex', alignItems:'baseline', gap:14, marginBottom:4,
        }}>
          <div style={{
            fontFamily:F.serif, fontSize:22, fontWeight:400,
            color:S.text, fontStyle:'italic',
          }}>
            {title}
          </div>
          <div style={{
            fontFamily:F.mono, fontSize:11, color:accent,
            letterSpacing:'0.06em',
          }}>
            {count} total
          </div>
        </div>
        <div style={{ fontSize:12, color:S.static }}>{subtitle}</div>
      </div>

      {/* Filter tabs */}
      {filterTabs && (
        <div style={{
          display:'flex', gap:0, padding:'0 28px',
          background:S.s1, borderBottom:`1px solid ${S.b1}`, flexShrink:0,
        }}>
          {filterTabs.map(t => {
            const on = activeFilter === t.id;
            return (
              <button
                key={t.id}
                onClick={() => onFilterChange(t.id)}
                style={{
                  padding:'8px 16px',
                  fontFamily:F.mono, fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase',
                  cursor:'pointer', textAlign:'center',
                  color: on ? accent : S.static,
                  border:'none', background:'transparent',
                  borderBottom:`2px solid ${on ? accent : 'transparent'}`,
                  marginBottom:-1, transition:'all .15s',
                }}
                onMouseEnter={e => { if (!on) e.currentTarget.style.color = S.mid; }}
                onMouseLeave={e => { if (!on) e.currentTarget.style.color = S.static; }}
              >
                {t.label}{typeof t.count === 'number' ? ` · ${t.count}` : ''}
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div style={{ flex:1, overflowY:'auto', padding:'18px 28px' }}>
        <div style={{ maxWidth:760, margin:'0 auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Cascade library ────────────────────────────────────────
function CascadeLibrary({ workspaceId, species }) {
  const [cascades, setCascades]   = useState(null);
  const [filter,   setFilter]     = useState('all');
  const [drafting, setDrafting]   = useState(false);
  const [draftFor, setDraftFor]   = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/cascades?workspace_id=${workspaceId}`);
    if (r.ok) { const j = await r.json(); setCascades(j.cascades || []); }
    else setCascades([]);
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  async function draft(triggerBinomial) {
    if (drafting) return;
    setDrafting(true); setDraftFor(triggerBinomial);
    try {
      await fetch('/api/continuum/propose-cascade', {
        method:'POST', headers:{'content-type':'application/json'},
        body: JSON.stringify({ workspace_id: workspaceId, trigger_binomial: triggerBinomial }),
      });
      await reload();
    } catch { /* swallow */ }
    setDrafting(false); setDraftFor(null);
  }
  async function accept(id)  { await fetch('/api/continuum/cascades', { method:'PATCH', headers:{'content-type':'application/json'}, body: JSON.stringify({ id, status:'committed' })}); reload(); }
  async function dismiss(id) { await fetch(`/api/continuum/cascades?id=${id}`, { method:'DELETE' }); reload(); }

  const all = cascades || [];
  const proposed  = all.filter(c => c.status === 'proposed');
  const committed = all.filter(c => c.status === 'committed');
  const filtered = filter === 'proposed' ? proposed
                 : filter === 'committed' ? committed
                 : [...proposed, ...committed];

  return (
    <LibraryShell
      title="Cascade Models"
      subtitle="Trophic cascade chains showing what follows when a trigger species disappears. Drafted by Shift from real species data; accept to commit."
      accent={S.gold}
      count={all.length}
      filterTabs={[
        { id:'all',       label:'All',       count: all.length },
        { id:'proposed',  label:'Proposed',  count: proposed.length },
        { id:'committed', label:'Committed', count: committed.length },
      ]}
      activeFilter={filter}
      onFilterChange={setFilter}
    >
      {cascades === null && (
        <div style={{ fontSize:12, color:S.dim, fontStyle:'italic', padding:'40px 0', textAlign:'center' }}>Loading…</div>
      )}
      {cascades !== null && filtered.length === 0 && (
        <div style={{ padding:'24px 0' }}>
          <div style={{ fontSize:13, color:S.static, lineHeight:1.7, marginBottom:14 }}>
            No cascades in this filter yet.
          </div>
          <DraftPicker species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} accent={S.gold} />
        </div>
      )}
      {filtered.map(c => (
        <CascadeCard
          key={c.id}
          cascade={c}
          onAccept={() => accept(c.id)}
          onDismiss={() => dismiss(c.id)}
        />
      ))}
      {filtered.length > 0 && (
        <DraftPicker species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} compact label="+ Draft another cascade" accent={S.gold} />
      )}
    </LibraryShell>
  );
}

// ── Story library ──────────────────────────────────────────
function StoryLibrary({ workspaceId, species }) {
  const [stories, setStories] = useState(null);
  const [filter,  setFilter]  = useState('all');
  const [drafting, setDrafting] = useState(false);
  const [draftFor, setDraftFor] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/stories?workspace_id=${workspaceId}`);
    if (r.ok) { const j = await r.json(); setStories(j.stories || []); }
    else setStories([]);
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  async function draft(triggerBinomial) {
    if (drafting) return;
    setDrafting(true); setDraftFor(triggerBinomial);
    try {
      await fetch('/api/continuum/propose-story', {
        method:'POST', headers:{'content-type':'application/json'},
        body: JSON.stringify({ workspace_id: workspaceId, trigger_binomial: triggerBinomial }),
      });
      await reload();
    } catch { /* swallow */ }
    setDrafting(false); setDraftFor(null);
  }
  async function accept(id) { await fetch('/api/continuum/stories', { method:'PATCH', headers:{'content-type':'application/json'}, body: JSON.stringify({ id, status:'committed' })}); reload(); }
  async function dismiss(id) { await fetch(`/api/continuum/stories?id=${id}`, { method:'DELETE' }); reload(); }
  async function markVerified(id) { await fetch('/api/continuum/stories', { method:'PATCH', headers:{'content-type':'application/json'}, body: JSON.stringify({ id, is_verified: true })}); reload(); }

  const all = stories || [];
  const proposed  = all.filter(s => s.status === 'proposed');
  const drafts    = all.filter(s => s.status === 'committed' && !s.is_verified);
  const verified  = all.filter(s => s.status === 'committed' && s.is_verified);
  const filtered = filter === 'proposed' ? proposed
                 : filter === 'draft'    ? drafts
                 : filter === 'verified' ? verified
                 : [...proposed, ...drafts, ...verified];

  return (
    <LibraryShell
      title="Story Engine"
      subtitle="Human-anchored narratives connecting a person to a species to an ecological mechanism. Drafted by Shift; mark verified after a real interview."
      accent={S.water}
      count={all.length}
      filterTabs={[
        { id:'all',       label:'All',       count: all.length },
        { id:'proposed',  label:'Proposed',  count: proposed.length },
        { id:'draft',     label:'Drafts',    count: drafts.length },
        { id:'verified',  label:'Verified',  count: verified.length },
      ]}
      activeFilter={filter}
      onFilterChange={setFilter}
    >
      {stories === null && (
        <div style={{ fontSize:12, color:S.dim, fontStyle:'italic', padding:'40px 0', textAlign:'center' }}>Loading…</div>
      )}
      {stories !== null && filtered.length === 0 && (
        <div style={{ padding:'24px 0' }}>
          <div style={{ fontSize:13, color:S.static, lineHeight:1.7, marginBottom:14 }}>
            No stories in this filter yet.
          </div>
          <DraftPicker species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} accent={S.water} />
        </div>
      )}
      {filtered.map(s => (
        <StoryCard
          key={s.id}
          story={s}
          onAccept={() => accept(s.id)}
          onDismiss={() => dismiss(s.id)}
          onMarkVerified={() => markVerified(s.id)}
        />
      ))}
      {filtered.length > 0 && (
        <DraftPicker species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} compact label="+ Draft another story" accent={S.water} />
      )}
    </LibraryShell>
  );
}

// ── Intervention library (with budget summary) ─────────────
function InterventionLibrary({ workspaceId, species }) {
  const [interventions, setInterventions] = useState(null);
  const [filter, setFilter] = useState('all');
  const [drafting, setDrafting] = useState(false);
  const [draftFor, setDraftFor] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/interventions?workspace_id=${workspaceId}&context_type=florida_indicator,manual`);
    if (r.ok) { const j = await r.json(); setInterventions(j.interventions || []); }
    else setInterventions([]);
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  async function draft(triggerBinomial) {
    if (drafting) return;
    setDrafting(true); setDraftFor(triggerBinomial);
    try {
      await fetch('/api/continuum/propose-intervention', {
        method:'POST', headers:{'content-type':'application/json'},
        body: JSON.stringify({ workspace_id: workspaceId, trigger_binomial: triggerBinomial }),
      });
      await reload();
    } catch { /* swallow */ }
    setDrafting(false); setDraftFor(null);
  }
  async function accept(id) { await fetch('/api/continuum/interventions', { method:'PATCH', headers:{'content-type':'application/json'}, body: JSON.stringify({ id, status:'committed' })}); reload(); }
  async function dismiss(id) { await fetch(`/api/continuum/interventions?id=${id}`, { method:'DELETE' }); reload(); }

  const all = interventions || [];
  const proposed  = all.filter(i => i.status === 'proposed');
  const committed = all.filter(i => i.status === 'committed');
  const filtered = filter === 'proposed' ? proposed
                 : filter === 'committed' ? committed
                 : [...proposed, ...committed];

  // Budget summary — only across committed interventions
  const totalCost = committed.reduce((sum, i) => sum + (Number(i.cost_amount) || 0), 0);
  const fmtCost = (n) => n >= 1_000_000 ? `$${(n/1_000_000).toFixed(1)}M`
                       : n >= 1_000     ? `$${Math.round(n/1_000)}K`
                       : `$${n.toLocaleString()}`;

  return (
    <LibraryShell
      title="Interventions"
      subtitle="Costed action plans designed to arrest cascades. Drafted by Shift; grounded in Conservation Evidence + USFWS recovery plans."
      accent={S.forest}
      count={all.length}
      filterTabs={[
        { id:'all',       label:'All',       count: all.length },
        { id:'proposed',  label:'Proposed',  count: proposed.length },
        { id:'committed', label:'Committed', count: committed.length },
      ]}
      activeFilter={filter}
      onFilterChange={setFilter}
    >
      {/* Budget summary across committed interventions */}
      {committed.length > 0 && (
        <div style={{
          background:'rgba(74,140,92,.06)', border:`1px solid ${S.forest}40`,
          borderRadius:8, padding:'14px 18px', marginBottom:18,
          display:'flex', alignItems:'center', gap:18,
        }}>
          <div>
            <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase', color:S.forest, marginBottom:4 }}>
              Total committed budget
            </div>
            <div style={{ fontFamily:F.mono, fontSize:24, fontWeight:500, color:S.forest }}>
              {fmtCost(totalCost)}
            </div>
          </div>
          <div style={{ width:1, height:36, background:S.b2 }} />
          <div style={{ fontSize:12, color:S.static, lineHeight:1.55 }}>
            Across {committed.length} {committed.length === 1 ? 'committed intervention' : 'committed interventions'}.
            Use this number when talking to funders.
          </div>
        </div>
      )}

      {interventions === null && (
        <div style={{ fontSize:12, color:S.dim, fontStyle:'italic', padding:'40px 0', textAlign:'center' }}>Loading…</div>
      )}
      {interventions !== null && filtered.length === 0 && (
        <div style={{ padding:'24px 0' }}>
          <div style={{ fontSize:13, color:S.static, lineHeight:1.7, marginBottom:14 }}>
            No interventions in this filter yet.
          </div>
          <DraftPicker species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} accent={S.forest} />
        </div>
      )}
      {filtered.map(iv => (
        <InterventionCard
          key={iv.id}
          intervention={iv}
          onAccept={() => accept(iv.id)}
          onDismiss={() => dismiss(iv.id)}
        />
      ))}
      {filtered.length > 0 && (
        <DraftPicker species={species} drafting={drafting} draftFor={draftFor} onDraft={draft} compact label="+ Draft another intervention" accent={S.forest} />
      )}
    </LibraryShell>
  );
}

function DmSection({ children }) {
  return (
    <div style={{
      fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
      color:S.static, margin:'14px 0 6px',
    }}>
      {children}
    </div>
  );
}
